import React from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { firmInfo } from '../../i18n/translations';

const Contact = ({ t, contactForm, setContactForm, handleContactSubmit, isSubmittingContact }) => {
  return (
    <section id="contact" className="py-20 px-4 bg-navy-50">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-navy-900 mb-4">{t.contact.title}</h2>
          <p className="text-lg text-gray-700">{t.contact.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-navy-900 mb-6">{t.contact.info.title}</h3>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-navy-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-navy-900 mb-1">{t.contact.info.email}</h4>
                  <a href={`mailto:${firmInfo.email}`} className="text-gray-700 hover:text-navy-600">
                    {firmInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-navy-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-navy-900 mb-1">{t.contact.info.phone}</h4>
                  <a href={`tel:${firmInfo.phone}`} className="text-gray-700 hover:text-navy-600">
                    {firmInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-navy-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-navy-900 mb-1">{t.contact.info.location}</h4>
                  <p className="text-gray-700">{t.contact.info.locationValue}</p>
                </div>
              </div>
            </div>
          </div>

          <Card className="bg-white border-navy-100 shadow-lg">
            <CardHeader>
              <CardTitle className="text-navy-900">{t.contact.form.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">{t.contact.form.name}{t.contact.form.required}</Label>
                  <Input
                    id="contact-name"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="border-gray-300 focus:border-navy-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-email">{t.contact.form.email}{t.contact.form.required}</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="border-gray-300 focus:border-navy-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-phone">{t.contact.form.phone}</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="border-gray-300 focus:border-navy-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-message">{t.contact.form.message}{t.contact.form.required}</Label>
                  <Textarea
                    id="contact-message"
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="border-gray-300 focus:border-navy-600"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-navy-600 hover:bg-navy-700"
                  disabled={isSubmittingContact}
                >
                  {isSubmittingContact ? t.contact.form.submitting : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      {t.contact.form.submit}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;